import React, { useState, useRef, useEffect } from "react";
import { FormGroup, Label, Input, ListGroup, ListGroupItem } from "reactstrap";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import InputError from "components/framework/errorHandling/InputError";
import { ErrorHandler } from "services/customHooks/useErrors";
import { KeyValuePair } from "services/types/KeyValuePair";

export type SearchableDropdownFormInputProps = {
  disabled?: boolean;
  required?: boolean;
  labelTranslationId?: string;
  labelClassName?: string;
  value?: string;
  errorHandler?: ErrorHandler;
  className?: string;
  handleInputChange: (value: string) => void;
  options: KeyValuePair<string, string>[];
  lg?: boolean;
  placeHolder?: string;
  readonly?: boolean;
};

export default function SearchableDropdownFormInput(props: SearchableDropdownFormInputProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const errors = props.errorHandler?.getErrors();


  const filteredOptions = searchTerm.trim() === "" 
  ? props.options  // Show all options when search is empty
  : props.options.filter((option) => {
      const searchLower = searchTerm.toLowerCase().trim();
      const valueLower = option.value.toLowerCase();
      const keyLower = option.key.toLowerCase();
      
      if (searchLower.length <= 1) {
        return keyLower.startsWith(searchLower);
      }
      
      const normalizedValue = valueLower.replace(/[\s-]/g, '');
      const normalizedSearch = searchLower.replace(/[\s-]/g, '');
      
      return (
        valueLower.startsWith(searchLower) ||
        normalizedValue.startsWith(normalizedSearch)
      );
    });

  const selectedOption = props.options.find((opt) => opt.key === props.value);
  const displayValue = selectedOption ? selectedOption.value : "";
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (key: string) => {
    if (props.errorHandler) {
      props.errorHandler.clear();
    }
    props.handleInputChange(key);
    setIsOpen(false);
    setSearchTerm("");
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.errorHandler) {
      props.errorHandler.clear();
    }
    props.handleInputChange("");
    setSearchTerm("");
  };
  
  return (
    <FormGroup className={classNames("mb-2", props.className)}>
      {props.labelTranslationId && (
        <Label
          className={classNames("form-control-label", props.labelClassName, {
            "pc-required-label": props.required,
          })}
        >
          <FormattedMessage id={props.labelTranslationId} />
        </Label>
      )}
      <div ref={dropdownRef} style={{ position: "relative" }}>
        <Input
          type="text"
          className={classNames({
            "pc-invalid-input": errors && errors.length > 0,
          })}
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !props.disabled && !props.readonly && setIsOpen(true)}
          placeholder={props.placeHolder || "Search..."}
          disabled={props.disabled || props.readonly}
          autoComplete="off"
          bsSize={props.lg ? "lg" : "sm"}
        />
        {displayValue && !isOpen && !props.disabled && !props.readonly && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0",
              fontSize: "18px",
              color: "#6c757d",
              lineHeight: "1",
            }}
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
        
        {isOpen && !props.disabled && !props.readonly && (
          <ListGroup
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 1050,
              marginTop: "1px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <ListGroupItem
                  key={option.key}
                  action
                  onClick={() => handleSelect(option.key)}
                  style={{ cursor: "pointer" }}
                  active={option.key === props.value}
                >
                  {option.value} ({option.key})                 
                </ListGroupItem>
              ))
            ) : (
              <ListGroupItem disabled>No results found</ListGroupItem>
            )}
          </ListGroup>
        )}
      </div>
      {errors && <InputError errors={errors} />
      }
    </FormGroup>
  );
}